import React, { useRef, useState, useEffect, useCallback } from 'react';
import PageTitle from '../../FormElements/PageTitle';
import LandingPageExitMenu from '../../Navigation/LandingPageExitMenu';
import getLandingImageHeight from '../../../utils/getLandingImageHeight';
import NutritionLabImage from '../../../../assets/images/nutrition_lab.jpg';
import SpinnerImage from '../../../../assets/images/spinner.gif';
import { compressImageForVision } from '../../../utils/compressImageForVision';
import { apiJson } from '../../../utils/userEndpoint';
import { SAVE_MY_FOOD_CUISINE_TYPES } from '../../../constants/saveMyFoodCuisineTypes';
import RecipeBookModal from '../../RecipeBook/RecipeBookModal';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';

const MAX_FILE_BYTES = 8 * 1024 * 1024;

const TIER_LABELS = {
	health_first: 'Healthiest',
	balanced: 'Balanced',
	indulgent_last: 'Treat',
	legacy_collective: 'Combined',
	legacy_individual: 'Single ingredient'
};

function tierLabelForHistory(tier) {
	return TIER_LABELS[tier] || tier || 'Recipe';
}

function recipeVariantForTier(tier) {
	if (tier === 'indulgent_last' || tier === 'legacy_individual') {
		return 'indulgent';
	}
	if (tier === 'balanced') {
		return 'balanced';
	}
	return 'default';
}

function formatScanDate(isoString) {
	if (!isoString) {
		return '';
	}
	const date = new Date(isoString);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	return date.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
}

function historyRecipeForDisplay(recipe) {
	return {
		id: `history-${recipe.id}`,
		title: recipe.title,
		description: recipe.description || '',
		health_badge: recipe.health_badge || '',
		source_url: recipe.source_url || '',
		key_ingredients: Array.isArray(recipe.key_ingredients) ? recipe.key_ingredients : [],
		full_recipe: {
			summary: recipe.full_recipe?.summary || '',
			ingredients: Array.isArray(recipe.full_recipe?.ingredients)
				? recipe.full_recipe.ingredients
				: [],
			steps: Array.isArray(recipe.full_recipe?.steps) ? recipe.full_recipe.steps : []
		}
	};
}

function toBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = () => reject(new Error('Could not read file.'));
		reader.readAsDataURL(file);
	});
}

function buildInventoryList(inventory) {
	if (!Array.isArray(inventory)) {
		return [];
	}

	return inventory.map((item, index) => {
		if (typeof item === 'string') {
			return { id: `ingredient-${index}`, name: item, condition: '', confidence: 1 };
		}

		return {
			id: item.id || `ingredient-${index}`,
			name: item.name || 'Unknown ingredient',
			condition: item.condition || item.notes || '',
			confidence: typeof item.confidence === 'number' ? item.confidence : 1,
			visual_evidence: item.visual_evidence || '',
			health_category: item.health_category || '',
			use_priority_hint: item.use_priority_hint || ''
		};
	});
}

function buildRecipeList(recipes) {
	if (!Array.isArray(recipes)) {
		return [];
	}

	return recipes.map((recipe, index) => {
		if (typeof recipe === 'string') {
			return {
				id: `recipe-${index}`,
				title: recipe,
				description: '',
				source_url: '',
				key_ingredients: [],
				full_recipe: {
					summary: '',
					ingredients: [],
					steps: []
				}
			};
		}

		return {
			id: recipe.id || `recipe-${index}`,
			title: recipe.title || recipe.name || 'Recipe idea',
			description: recipe.description || recipe.why || '',
			health_badge: recipe.health_badge || '',
			source_url: recipe.source_url || '',
			key_ingredients: Array.isArray(recipe.key_ingredients)
				? recipe.key_ingredients
				: [],
			full_recipe: {
				summary: recipe.full_recipe?.summary || '',
				ingredients: Array.isArray(recipe.full_recipe?.ingredients)
					? recipe.full_recipe.ingredients
					: [],
				steps: Array.isArray(recipe.full_recipe?.steps)
					? recipe.full_recipe.steps
					: []
			}
		};
	});
}

export default function NutritionLabPage({
	analyze_food_photos_endpoint,
	ai_recipe_history_endpoint,
	recipe_catalog_endpoint,
	recipe_catalog_suggest_endpoint
}) {
	const { guardSubscription } = useSubscriptionGate();
	const [isRecipeBookOpen, setIsRecipeBookOpen] = useState(false);
	const [isFoodHelperModalOpen, setIsFoodHelperModalOpen] = useState(false);
	const [modalTab, setModalTab] = useState('scan');
	const [selectedImages, setSelectedImages] = useState([]);
	const [foodHint, setFoodHint] = useState('');
	const [cuisineType, setCuisineType] = useState('');
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [analyzingPhase, setAnalyzingPhase] = useState('');
	const [error, setError] = useState('');
	const [analysis, setAnalysis] = useState(null);
	const [historyBatches, setHistoryBatches] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState('');
	const [expandedRecipeId, setExpandedRecipeId] = useState(null);
	const cameraInputRef = useRef(null);
	const uploadInputRef = useRef(null);
	const foodModalRef = useRef(null);

	const resetModalState = useCallback(() => {
		setSelectedImages((current) => {
			current.forEach((image) => {
				if (image.previewUrl) {
					URL.revokeObjectURL(image.previewUrl);
				}
			});
			return [];
		});
		setFoodHint('');
		setCuisineType('');
		setError('');
		setAnalysis(null);
		setIsAnalyzing(false);
		setAnalyzingPhase('');
		if (cameraInputRef.current) {
			cameraInputRef.current.value = '';
		}
		if (uploadInputRef.current) {
			uploadInputRef.current.value = '';
		}
	}, []);

	const closeModal = useCallback(() => {
		resetModalState();
		setModalTab('scan');
		setHistoryBatches([]);
		setHistoryError('');
		setExpandedRecipeId(null);
		setIsFoodHelperModalOpen(false);
	}, [resetModalState]);

	const fetchHistory = useCallback(async () => {
		if (!ai_recipe_history_endpoint) {
			setHistoryError('Recipe history is unavailable right now.');
			return;
		}

		setHistoryLoading(true);
		setHistoryError('');
		try {
			const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
			const payload = await apiJson(ai_recipe_history_endpoint, { csrfToken });
			setHistoryBatches(Array.isArray(payload.data?.batches) ? payload.data.batches : []);
		} catch (err) {
			setHistoryBatches([]);
			setHistoryError(err.message || 'Could not load recipe history.');
		} finally {
			setHistoryLoading(false);
		}
	}, [ai_recipe_history_endpoint]);

	useEffect(() => {
		if (!isFoodHelperModalOpen || modalTab !== 'history') {
			return;
		}
		fetchHistory();
	}, [isFoodHelperModalOpen, modalTab, fetchHistory]);

	const addFiles = (files) => {
		const file = files?.[0];
		if (!file) {
			return;
		}

		setError('');
		setAnalysis(null);

		if (!file.type.startsWith('image/')) {
			setError('Please upload an image file.');
			return;
		}

		if (file.size > MAX_FILE_BYTES) {
			setError('Image must be under 8MB.');
			return;
		}

		setSelectedImages((current) => {
			current.forEach((image) => {
				if (image.previewUrl) {
					URL.revokeObjectURL(image.previewUrl);
				}
			});
			return [
				{
					id: `${Date.now()}-${Math.random()}`,
					file,
					previewUrl: URL.createObjectURL(file)
				}
			];
		});
	};

	const removeImage = (id) => {
		setError('');
		setAnalysis(null);
		setSelectedImages((current) => {
			const target = current.find((image) => image.id === id);
			if (target?.previewUrl) {
				URL.revokeObjectURL(target.previewUrl);
			}
			return current.filter((image) => image.id !== id);
		});
	};

	useEffect(() => {
		if (!isFoodHelperModalOpen) {
			return undefined;
		}
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const handleMouseDown = (event) => {
			if (foodModalRef.current && !foodModalRef.current.contains(event.target)) {
				closeModal();
			}
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => {
			document.body.style.overflow = prevOverflow;
			document.removeEventListener('mousedown', handleMouseDown);
		};
	}, [isFoodHelperModalOpen, closeModal]);

	const recipeVariantForSectionKey = (key) => {
		if (key === 'indulgent_last') return 'indulgent';
		if (key === 'balanced') return 'balanced';
		return 'default';
	};

	const postFoodAnalysis = async (body) => {
		const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
		const response = await fetch(analyze_food_photos_endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': csrfToken
			},
			body: JSON.stringify(body)
		});

		let payload;
		try {
			payload = await response.json();
		} catch (_parseError) {
			const timedOut = response.status === 503 || response.status === 504;
			throw new Error(
				timedOut
					? 'Food analysis timed out. Try a smaller photo or a shorter description.'
					: 'Could not analyze your photos right now.'
			);
		}

		if (!response.ok || !payload.success) {
			throw new Error(payload.error || 'Could not analyze your photos right now.');
		}

		return payload;
	};

	const handleAnalyze = async () => {
		if (!analyze_food_photos_endpoint) {
			setError('Save My Food is unavailable right now.');
			return;
		}

		const hintTrimmed = foodHint.trim();
		const hasPhoto = selectedImages.length > 0;
		const hasDescription = hintTrimmed.length > 0;
		if (!hasPhoto && !hasDescription) {
			setError('Add a photo, describe what you have, or both.');
			return;
		}

		setError('');
		setAnalysis(null);
		setIsAnalyzing(true);
		try {
			const imageDataUrls = selectedImages.length
				? await Promise.all(
						selectedImages.map(async (image) => {
							const compressed = await compressImageForVision(image.file);
							return toBase64(compressed);
						})
					)
				: [];

			setAnalyzingPhase('Identifying ingredients…');
			const extractPayload = await postFoodAnalysis({
				images: imageDataUrls,
				food_hint: hintTrimmed || undefined,
				cuisine_type: cuisineType || undefined
			});

			setAnalyzingPhase('Generating recipes…');
			const analyzePayload = await postFoodAnalysis({
				inventory_analysis: extractPayload.data,
				food_hint: hintTrimmed || undefined,
				cuisine_type: cuisineType || undefined,
				text_only: extractPayload.data?.text_only
			});

			setAnalysis(analyzePayload.data);
			fetchHistory();
		} catch (analyzeError) {
			setError(analyzeError.message || 'Could not analyze your photos right now.');
		} finally {
			setIsAnalyzing(false);
			setAnalyzingPhase('');
		}
	};

	const inventoryItems = buildInventoryList(analysis?.inventory);
	const recipeSections = Array.isArray(analysis?.recipe_sections)
		? analysis.recipe_sections.map((section, idx) => ({
				key: section.key || `section-${idx}`,
				title: section.title || 'Recipes',
				subtitle: section.subtitle || '',
				recipes: buildRecipeList(section.recipes)
			}))
		: [];
	const hasStructuredRecipeSections = recipeSections.some((s) => s.recipes.length > 0);

	const collectiveRecipes = buildRecipeList(analysis?.recipes?.collective);
	const individualRecipes = buildRecipeList(analysis?.recipes?.individual);
	const mergedRecipeIdeas = [
		...collectiveRecipes.map((recipe) => ({
			...recipe,
			recipe_type: 'collective'
		})),
		...individualRecipes.map((recipe) => ({
			...recipe,
			recipe_type: 'individual'
		}))
	];

	const executiveSummary = analysis?.executive_summary;
	const actionPlan = analysis?.action_plan;
	const hasScanPhoto = selectedImages.length > 0;
	const hasScanDescription = foodHint.trim().length > 0;
	const canSubmitScan = hasScanPhoto || hasScanDescription;

	const sectionShellClass =
		'border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-card overflow-hidden';
	const sectionHeaderClass =
		'px-4 py-3 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800';

	const renderRecipeDetails = (recipe, options = {}) => {
		const { variant = 'default' } = options;
		const badgeClass =
			variant === 'indulgent'
				? 'text-amber-800 dark:text-amber-300'
				: variant === 'balanced'
					? 'text-blue-800 dark:text-blue-300'
					: 'text-green-700 dark:text-green-400';

		return (
		<div className="mt-2 space-y-2">
			{recipe.recipe_type === 'individual' ? (
				<p className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-semibold">
					Single ingredient fallback
				</p>
			) : null}
			{recipe.health_badge ? (
				<p className={`text-[11px] uppercase tracking-wide font-semibold ${badgeClass}`}>
					{recipe.health_badge}
				</p>
			) : null}
			{recipe.description ? (
				<p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{recipe.description}</p>
			) : null}
			{recipe.source_verified && recipe.source_url ? (
				<a
					href={recipe.source_url}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-block text-xs text-blue-700 dark:text-blue-400 underline break-all"
				>
					View verified source recipe
				</a>
			) : null}
			{recipe.key_ingredients.length ? (
				<p className="text-xs text-blue-700 dark:text-blue-400">
					Use: {recipe.key_ingredients.join(', ')}
				</p>
			) : null}
			{recipe.full_recipe?.summary ? (
				<p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
					{recipe.full_recipe.summary}
				</p>
			) : null}
			{recipe.full_recipe?.ingredients?.length ? (
				<div>
					<p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
						Ingredients
					</p>
					<ul className="mt-1 list-disc pl-4 space-y-1">
						{recipe.full_recipe.ingredients.map((ingredient, index) => (
							<li
								key={`${recipe.id}-ingredient-${index}`}
								className="text-xs text-gray-600 dark:text-gray-400"
							>
								{ingredient}
							</li>
						))}
					</ul>
				</div>
			) : null}
			{recipe.full_recipe?.steps?.length ? (
				<div>
					<p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
						Steps
					</p>
					<ol className="mt-1 list-decimal pl-4 space-y-1">
						{recipe.full_recipe.steps.map((step, index) => (
							<li key={`${recipe.id}-step-${index}`} className="text-xs text-gray-600 dark:text-gray-400">
								{step}
							</li>
						))}
					</ol>
				</div>
			) : null}
		</div>
		);
	};

	return (
		<div className="container p-4 pt-4 lg:py-16 md:px-20">
			<div>
				<PageTitle title="Nutrition Lab" />
			</div>

			<div className="relative mt-4 rounded-lg overflow-hidden shadow-lg">
				<img
					src={NutritionLabImage}
					alt="Nutrition Lab"
					decoding="async"
					className="block w-full object-cover object-center"
					style={getLandingImageHeight()}
				/>

				<div className="absolute bottom-2 md:bottom-4 left-1/2 z-10 -translate-x-1/2 transform">
					<div className="flex items-center justify-center gap-6 p-3 rounded-full">
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => (window.location.href = '/nutrition-lab')}
							title="Meal Plans (coming soon)"
						>
							📋
						</div>
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() =>
								window.open(
									'https://foodstruct.com/',
									'_blank',
									'noopener,noreferrer'
								)}
							title="FoodStruct"
						>
							🍎
						</div>
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => guardSubscription(() => setIsFoodHelperModalOpen(true))}
							title="Save My Food AI Helper"
						>
							📷
						</div>
						<div
							className="w-12 h-12 flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
							onClick={() => guardSubscription(() => setIsRecipeBookOpen(true))}
							title="Community Cookbook"
						>
							📖
						</div>
						<LandingPageExitMenu
							currentPage="nutrition-lab"
							triggerTitle="Leave Nutrition Lab"
							containerClassName="relative w-12 h-12 md:hidden"
							triggerClassName="w-full h-full flex items-center justify-center bg-blue-500 border border-gray-200 text-white text-2xl rounded-full cursor-pointer hover:scale-105 transition-transform transform backdrop-blur-md"
						/>
					</div>
				</div>
				<div className="hidden md:block absolute bottom-4 right-4 z-10">
					<LandingPageExitMenu
						currentPage="nutrition-lab"
						triggerTitle="Leave Nutrition Lab"
					/>
				</div>
			</div>

			{isFoodHelperModalOpen && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center p-4"
					style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
				>
					<div
						ref={foodModalRef}
						className="relative flex flex-col bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-lg max-w-xl w-full overflow-hidden"
						style={{ maxHeight: '90vh' }}
					>
						<div className="relative shrink-0 px-8 pt-6 pb-0 pr-14 border-b border-gray-200 dark:border-dark-border">
							<h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Save My Food!</h2>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 max-w-lg">
								{modalTab === 'history'
									? 'Recipes from your past photo scans.'
									: "Add a photo, describe what you have, or both. We'll provide three recipes to help you save your food."}
							</p>
							<div
								className="flex gap-6 mt-4"
								role="tablist"
								aria-label="Save My Food sections"
							>
								<button
									type="button"
									role="tab"
									aria-selected={modalTab === 'scan'}
									onClick={() => {
										setModalTab('scan');
										setExpandedRecipeId(null);
									}}
									className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
										modalTab === 'scan'
											? 'border-blue-500 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
									}`}
								>
									Scan
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={modalTab === 'history'}
									onClick={() => {
										setModalTab('history');
										setExpandedRecipeId(null);
									}}
									className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
										modalTab === 'history'
											? 'border-blue-500 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
									}`}
								>
									History
								</button>
							</div>
							<button
								type="button"
								className="absolute top-4 right-2 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition duration-300"
								onClick={closeModal}
								aria-label="Close Save My Food modal"
							>
								<svg
									className="h-6 w-6 text-gray-500 dark:text-gray-300"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						<div className="flex flex-col flex-1 min-h-0 overflow-hidden">
							<div className="flex-1 overflow-y-auto min-h-0 px-8 py-4 space-y-4">
								{modalTab === 'history' ? (
									<div className="space-y-4">
										{historyLoading ? (
											<div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
												<img src={SpinnerImage} alt="Loading" className="w-4 h-4 object-contain" />
												Loading your saved recipes…
											</div>
										) : null}
										{historyError ? (
											<p className="text-sm text-red-500">{historyError}</p>
										) : null}
										{!historyLoading && !historyError && historyBatches.length === 0 ? (
											<p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
												No saved scans yet. Analyze a food photo on the Scan tab — recipes from each
												run appear here.
											</p>
										) : null}
										{!historyLoading &&
											historyBatches.map((batch) => (
												<div key={batch.batch_id} className={sectionShellClass}>
													<p className="px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-dark-border">
														Scan · {formatScanDate(batch.created_at)}
													</p>
													<ul className="list-none m-0 p-0">
														{Array.isArray(batch.recipes) &&
															batch.recipes.map((recipe, recipeIndex) => {
																const displayRecipe = historyRecipeForDisplay(recipe);
																const isExpanded = expandedRecipeId === recipe.id;
																const variant = recipeVariantForTier(recipe.health_tier);
																const tierClass =
																	recipe.health_tier === 'indulgent_last'
																		? 'text-amber-700 dark:text-amber-400'
																		: recipe.health_tier === 'balanced'
																			? 'text-blue-700 dark:text-blue-400'
																			: 'text-green-700 dark:text-green-400';
																const showDivider =
																	recipeIndex < batch.recipes.length - 1;

																const toggleExpanded = () =>
																	setExpandedRecipeId(
																		isExpanded ? null : recipe.id
																	);

																return (
																	<li
																		key={recipe.id}
																		className={
																			showDivider
																				? 'border-b border-gray-200 dark:border-dark-border'
																				: ''
																		}
																	>
																		<div
																			role="button"
																			tabIndex={0}
																			aria-expanded={isExpanded}
																			className="flex items-center justify-between gap-3 px-4 py-3.5 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-800/40 active:bg-gray-100 dark:active:bg-gray-800/60"
																			onClick={toggleExpanded}
																			onKeyDown={(event) => {
																				if (
																					event.key === 'Enter' ||
																					event.key === ' '
																				) {
																					event.preventDefault();
																					toggleExpanded();
																				}
																			}}
																		>
																			<div className="min-w-0 flex-1">
																				<p className="text-sm font-medium text-gray-900 dark:text-dark-text leading-snug">
																					{displayRecipe.title}
																				</p>
																				<p
																					className={`text-[11px] uppercase tracking-wide mt-0.5 ${tierClass}`}
																				>
																					{tierLabelForHistory(recipe.health_tier)}
																				</p>
																			</div>
																			<svg
																				className={`h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${
																					isExpanded ? 'rotate-180' : ''
																				}`}
																				fill="none"
																				viewBox="0 0 24 24"
																				stroke="currentColor"
																				aria-hidden
																			>
																				<path
																					strokeLinecap="round"
																					strokeLinejoin="round"
																					strokeWidth="2"
																					d="M19 9l-7 7-7-7"
																				/>
																			</svg>
																		</div>
																		{isExpanded ? (
																			<div className="px-4 pb-4 pt-0 bg-gray-50 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-30">
																				{renderRecipeDetails(displayRecipe, {
																					variant
																				})}
																			</div>
																		) : null}
																	</li>
																);
															})}
													</ul>
												</div>
											))}
									</div>
								) : (
									<>
								<input
									ref={cameraInputRef}
									type="file"
									accept="image/*"
									capture="environment"
									className="hidden"
									onChange={(event) => {
										addFiles(Array.from(event.target.files || []));
										event.target.value = '';
									}}
								/>
								<input
									ref={uploadInputRef}
									type="file"
									accept="image/*"
									className="hidden"
									onChange={(event) => {
										addFiles(Array.from(event.target.files || []));
										event.target.value = '';
									}}
								/>

								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									What food do you have?
									{selectedImages.length ? (
										<span className="font-normal text-gray-500 dark:text-gray-400"> (optional)</span>
									) : null}
									<textarea
										value={foodHint}
										onChange={(event) => {
											setFoodHint(event.target.value);
											if (event.target.value.trim() || selectedImages.length) {
												setError('');
											}
										}}
										disabled={isAnalyzing}
										placeholder="e.g. ripe guava, leftover roast chicken"
										rows={2}
										className="mt-1.5 w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-60"
									/>
								</label>
								<p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
									{selectedImages.length
										? 'Helps when the photo is unclear. Use with a new photo if the first scan misidentified the food.'
										: 'A photo is optional if you describe your food above.'}
								</p>

								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Cuisine type
									<span className="font-normal text-gray-500 dark:text-gray-400"> (optional)</span>
									<select
										value={cuisineType}
										onChange={(event) => setCuisineType(event.target.value)}
										disabled={isAnalyzing}
										className="mt-1.5 w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2 text-sm text-gray-900 dark:text-dark-text disabled:opacity-60"
									>
										<option value="">Any — no preference</option>
										{SAVE_MY_FOOD_CUISINE_TYPES.map((cuisine) => (
											<option key={cuisine} value={cuisine}>
												{cuisine}
											</option>
										))}
									</select>
								</label>
								<p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
									Real dishes from that cuisine only — we will not slap the cuisine name on oatmeal
									or banana bread. If your ingredients do not fit, tiers use our usual ideas with no
									cuisine label.
								</p>

								<div className="flex flex-wrap gap-2 items-center">
									<button
										type="button"
										onClick={() => cameraInputRef.current?.click()}
										disabled={isAnalyzing}
										className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
									>
										{selectedImages.length ? 'Retake photo' : 'Take photo'}
									</button>
									<button
										type="button"
										onClick={() => uploadInputRef.current?.click()}
										disabled={isAnalyzing}
										className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
									>
										{selectedImages.length ? 'Replace photo' : 'Upload photo'}
									</button>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										PNG/JPG/WEBP · 8MB max
									</p>
								</div>

								{selectedImages.map((image) => (
									<div
										key={image.id}
										className="relative rounded-lg border border-gray-300 dark:border-dark-border overflow-hidden bg-gray-50 dark:bg-dark-bg"
									>
										<img
											src={image.previewUrl}
											alt="Food preview"
											className="w-full max-h-56 object-cover"
										/>
										<button
											type="button"
											onClick={() => removeImage(image.id)}
											disabled={isAnalyzing}
											className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black bg-opacity-75 text-white text-lg leading-none flex items-center justify-center hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
											aria-label="Remove photo"
										>
											×
										</button>
									</div>
								))}

								{error ? (
									<p className="text-sm text-red-500" role="alert">
										{error}
									</p>
								) : null}
								{analysis?.warning ? (
									<div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 px-3 py-2">
										<p className="text-sm text-amber-800 dark:text-amber-200">{analysis.warning}</p>
									</div>
								) : null}

								{isAnalyzing ? (
									<div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
										<img src={SpinnerImage} alt="Loading" className="w-4 h-4 object-contain" />
										{analyzingPhase || 'Analyzing… this can take up to a minute.'}
									</div>
								) : null}

								{analysis && executiveSummary ? (
									<div className={sectionShellClass}>
										<div className={sectionHeaderClass}>
											<h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
												Your plan (health first)
											</h4>
										</div>
										<div className="p-4 space-y-3">
											{executiveSummary.headline ? (
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text leading-snug">
													{executiveSummary.headline}
												</p>
											) : null}
											{executiveSummary.health_rationale ? (
												<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
													{executiveSummary.health_rationale}
												</p>
											) : null}
										</div>
									</div>
								) : null}

								{analysis && actionPlan ? (
									<div className={sectionShellClass}>
										<div className={sectionHeaderClass}>
											<h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
												Action plan
											</h4>
										</div>
										<div className="p-4 space-y-4 text-sm">
											{actionPlan.primary ? (
												<div>
													<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
														Priority
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
														{actionPlan.primary}
													</p>
												</div>
											) : null}
											{actionPlan.secondary ? (
												<div>
													<p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
														Also do
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
														{actionPlan.secondary}
													</p>
												</div>
											) : null}
											{actionPlan.nutrition_tip ? (
												<div className="rounded-lg border border-green-300 bg-green-50 p-3 dark:border-green-700 dark:bg-green-900 dark:bg-opacity-30">
													<p className="text-xs font-bold uppercase tracking-wider mb-1 text-green-800 dark:text-green-300">
														Nutrition tip
													</p>
													<p className="leading-relaxed text-green-900 dark:text-green-200">
														{actionPlan.nutrition_tip}
													</p>
												</div>
											) : null}
										</div>
									</div>
								) : null}

								{analysis && inventoryItems.length > 0 ? (
									<div className={sectionShellClass}>
										<div className={sectionHeaderClass}>
											<h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
												Detected ingredients
											</h4>
										</div>
										<div className="p-4">
											<ul className="space-y-3">
												{inventoryItems.map((item) => (
													<li
														key={item.id}
														className="text-sm text-gray-800 dark:text-gray-200 border-l-4 border-gray-300 dark:border-gray-600 pl-3 py-1"
													>
														<span className="font-semibold">{item.name}</span>
														{item.condition ? (
															<span className="text-gray-600 dark:text-gray-400">
																{' '}
																— {item.condition}
															</span>
														) : null}
														{item.confidence !== null ? (
															<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
																({Math.round(item.confidence * 100)}% confidence)
															</span>
														) : null}
														{item.health_category || item.use_priority_hint ? (
															<p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">
																{[item.use_priority_hint, item.health_category]
																	.filter(Boolean)
																	.join(' · ')}
															</p>
														) : null}
														{item.visual_evidence ? (
															<p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
																{item.visual_evidence}
															</p>
														) : null}
													</li>
												))}
											</ul>
										</div>
									</div>
								) : null}

								{analysis && hasStructuredRecipeSections
									? recipeSections
											.filter((section) => section.recipes.length > 0)
											.map((section) => {
												const variant = recipeVariantForSectionKey(section.key);
												const borderAccent =
													section.key === 'indulgent_last'
														? 'border-amber-400 dark:border-amber-600'
														: section.key === 'balanced'
															? 'border-blue-400 dark:border-blue-700'
															: 'border-green-500 dark:border-green-600';
												return (
													<div
														key={section.key}
														className={`${sectionShellClass} border-l-4 ${borderAccent}`}
													>
														<div className={sectionHeaderClass}>
															<h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
																{section.title}
															</h4>
															{section.subtitle ? (
																<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
																	{section.subtitle}
																</p>
															) : null}
														</div>
														<div className="p-4 space-y-4">
															{section.recipes.map((recipe) => (
																<div
																	key={recipe.id}
																	className="pb-4 border-b border-gray-100 dark:border-dark-border last:border-0 last:pb-0"
																>
																	<p className="font-semibold text-gray-900 dark:text-dark-text">
																		{recipe.title}
																	</p>
																	{renderRecipeDetails(recipe, { variant })}
																</div>
															))}
														</div>
													</div>
												);
											})
									: null}

								{analysis && !hasStructuredRecipeSections && mergedRecipeIdeas.length > 0 ? (
									<div className={sectionShellClass}>
										<div className={sectionHeaderClass}>
											<h4 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
												Recipe ideas
											</h4>
											<p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
												Combined and single-ingredient ideas from your photo.
											</p>
										</div>
										<div className="p-4 space-y-4">
											{mergedRecipeIdeas.map((recipe) => (
												<div
													key={recipe.id}
													className="pb-4 border-b border-gray-100 dark:border-dark-border last:border-0 last:pb-0"
												>
													<p className="font-semibold text-gray-900 dark:text-dark-text">
														{recipe.title}
													</p>
													{renderRecipeDetails(recipe, { variant: 'default' })}
												</div>
											))}
										</div>
									</div>
								) : null}

								{analysis && !isAnalyzing ? (
									<p className="text-xs text-gray-500 dark:text-gray-400">
										AI suggests public recipe links. Verify ingredients for allergies and dietary
										needs.
									</p>
								) : null}
									</>
								)}
							</div>

							<div className="shrink-0 flex flex-wrap justify-end gap-2 px-8 py-4 border-t border-gray-200 dark:border-dark-border">
								<button
									type="button"
									className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
									onClick={closeModal}
								>
									{modalTab === 'history' ? 'Close' : 'Cancel'}
								</button>
								{modalTab === 'scan' ? (
									<button
										type="button"
										onClick={() => {
											if (!canSubmitScan) {
												setError('Add a photo, describe what you have, or both.');
												return;
											}
											handleAnalyze();
										}}
										disabled={isAnalyzing}
										className={`px-4 py-2 rounded-lg text-white font-medium bg-blue-500 ${
											canSubmitScan && !isAnalyzing
												? 'hover:bg-blue-600'
												: 'opacity-50 cursor-not-allowed'
										}`}
									>
										{isAnalyzing
											? 'Analyzing…'
											: analysis
												? 'Analyze again'
												: selectedImages.length
													? 'Analyze food'
													: 'Get recipes'}
									</button>
								) : (
									<button
										type="button"
										onClick={fetchHistory}
										disabled={historyLoading}
										className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
									>
										{historyLoading ? 'Refreshing…' : 'Refresh'}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			<RecipeBookModal
				isOpen={isRecipeBookOpen}
				onClose={() => setIsRecipeBookOpen(false)}
				recipeCatalogEndpoint={recipe_catalog_endpoint}
				recipeCatalogSuggestEndpoint={recipe_catalog_suggest_endpoint}
			/>
		</div>
	);
}
